import { Autocomplete, TextField } from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import AddIcon from '@mui/icons-material/Add';
import type { Category } from '../types';

const CREATE_SENTINEL = '__create__';
const NO_ELIGIBLE_CATEGORIES_SENTINEL = '__no_eligible_categories__';
const categoryFilter = createFilterOptions<Category>({ stringify: (option) => option.name });

interface CategoryAutocompleteFieldProps {
  // Eligible categories to offer — already filtered by the caller (e.g. via
  // useCategoryLinkedOptions) to those compatible with whatever is already selected elsewhere
  // on the form.
  categories: Category[];
  value: Category | string | null;
  inputValue: string;
  hasNoEligibleCategories: boolean;
  label: string;
  addCategoryLabel: string;
  noEligibleCategoriesLabel: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  onSelectCategory: (category: Category) => void;
  onFreeTextChange: (text: string) => void;
  onCreateNew: () => void;
}

// Shared "Categorie" freeSolo Autocomplete (a real managed category, free text, or nothing) with
// a "+ Nieuwe categorie toevoegen" quick-create row and, when the caller reports no eligible
// categories for whatever is already selected, a disabled explanatory row — used by both
// MachineFormPage (create) and DetailActionDialog (edit) so the two forms can't drift apart.
export default function CategoryAutocompleteField({
  categories,
  value,
  inputValue,
  hasNoEligibleCategories,
  label,
  addCategoryLabel,
  noEligibleCategoriesLabel,
  required,
  error,
  helperText,
  onSelectCategory,
  onFreeTextChange,
  onCreateNew,
}: CategoryAutocompleteFieldProps) {
  const createSentinel = (): Category => ({
    id: CREATE_SENTINEL,
    name: addCategoryLabel,
    organization_id: '',
    archived: false,
    created_at: '',
  });

  return (
    <Autocomplete
      freeSolo
      options={categories}
      filterOptions={(opts, params) => [
        createSentinel(),
        ...(hasNoEligibleCategories
          ? [{ id: NO_ELIGIBLE_CATEGORIES_SENTINEL, name: noEligibleCategoriesLabel, organization_id: '', archived: false, created_at: '' }]
          : []),
        ...categoryFilter(opts, params),
      ]}
      getOptionDisabled={(opt) => typeof opt !== 'string' && opt.id === NO_ELIGIBLE_CATEGORIES_SENTINEL}
      getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.name)}
      isOptionEqualToValue={(opt, val) => typeof opt !== 'string' && typeof val !== 'string' && opt.id === val.id}
      value={value}
      inputValue={inputValue}
      onChange={(_, newValue) => {
        if (newValue !== null && typeof newValue !== 'string' && newValue.id === CREATE_SENTINEL) {
          onCreateNew();
          return;
        }
        if (newValue === null) onFreeTextChange('');
        else if (typeof newValue === 'string') onFreeTextChange(newValue);
        else onSelectCategory(newValue);
      }}
      onInputChange={(_, newInputValue, reason) => {
        if (reason === 'input') onFreeTextChange(newInputValue);
      }}
      renderOption={({ key, ...props }, opt) => {
        const isCreate = opt.id === CREATE_SENTINEL;
        const isInfo = opt.id === NO_ELIGIBLE_CATEGORIES_SENTINEL;
        return (
          <li
            key={key}
            {...props}
            data-testid={isInfo ? 'no-eligible-categories-option' : `option-${opt.id}`}
            className={
              isCreate
                ? `${props.className ?? ''} text-primary`
                : isInfo
                  ? `${props.className ?? ''} text-text-secondary`
                  : props.className
            }
          >
            {isCreate && <AddIcon fontSize="small" className="mr-1" />}
            {opt.name}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={error}
          helperText={helperText}
          fullWidth
          slotProps={{
            ...params.slotProps,
            htmlInput: { ...params.slotProps.htmlInput, 'aria-label': label },
          }}
        />
      )}
    />
  );
}
